import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Activity, Settings } from 'lucide-react';
import { AgentMetricsCard } from '../AgentMetricsCard';

interface AgentCardProps {
  agent: {
    id: string;
    name: string;
    description: string;
    status: string; // the recorded agents.status word
    type: string;
    lastActive?: string;
    metrics?: {
      requests?: number;
      avgResponseTime?: number;
      successRate?: number;
    };
  };
  onConfigure?: (agentId: string) => void;
  onViewDetails?: (agentId: string) => void;
  showRealTimeMetrics?: boolean;
}

const AgentCard: React.FC<AgentCardProps> = ({ agent, onConfigure, onViewDetails, showRealTimeMetrics = false }) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'running':
      case 'active':
        return 'bg-green-500';
      case 'stopped':
      case 'paused':
        return 'bg-gray-500';
      case 'error':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  return (
    <Card className="hover:shadow-lg transition-shadow">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-lg">{agent.name}</CardTitle>
            <CardDescription className="mt-1">{agent.description}</CardDescription>
          </div>
          <Badge variant="outline" className="ml-2">
            {agent.type}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${getStatusColor(agent.status)}`} />
            <span className="text-sm capitalize">{agent.status}</span>
            {agent.lastActive && (
              <span className="text-sm text-muted-foreground ml-auto">
                Last active: {agent.lastActive}
              </span>
            )}
          </div>
          
          {showRealTimeMetrics ? (
            <AgentMetricsCard agentName={agent.name} compact={true} />
          ) : (
            agent.metrics && (
              <div className="grid grid-cols-3 gap-2 text-sm">
                {agent.metrics.requests !== undefined && (
                  <div>
                    <p className="text-muted-foreground">Requests</p>
                    <p className="font-medium">{agent.metrics.requests.toLocaleString()}</p>
                  </div>
                )}
                {agent.metrics.avgResponseTime !== undefined && (
                  <div>
                    <p className="text-muted-foreground">Avg Response</p>
                    <p className="font-medium">{agent.metrics.avgResponseTime}ms</p>
                  </div>
                )}
                {agent.metrics.successRate !== undefined && (
                  <div>
                    <p className="text-muted-foreground">Success Rate</p>
                    <p className="font-medium">{agent.metrics.successRate}%</p>
                  </div>
                )}
              </div>
            )
          )}
          
          <div className="flex gap-2">
            {onViewDetails && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => onViewDetails(agent.id)}
                className="flex-1"
              >
                <Activity className="h-4 w-4 mr-1" />
                View Details
              </Button>
            )}
            {onConfigure && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => onConfigure(agent.id)}
                className="flex-1"
              >
                <Settings className="h-4 w-4 mr-1" />
                Configure
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export { AgentCard };